import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw, XCircle } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { api } from '../services/api';
import { handleApiError } from '../utils/errorHandling';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import {
    CatalogSyncRunSummary,
    DataHealthReport,
    DataHealthState,
    DocumentIngestionHealth,
} from '../types';

/**
 * Whether what the assistant knows is still true.
 *
 * The page answers three questions in the order a merchant actually asks them: is something wrong,
 * what is the assistant currently unable to find, and what has the sync been doing. The verdict is
 * first and large because it is the only part most people will read on most days.
 *
 * Nothing here is live-critical, so the refresh is slow and manual is always available — a sync log
 * that polls every few seconds would suggest an urgency the data does not have.
 */
const POLL_MS = 60000;

const VERDICTS: Record<DataHealthState, { label: string; detail: string; tone: string; Icon: typeof CheckCircle2 }> = {
    healthy: {
        label: 'Up to date',
        detail: 'The catalog is current and everything stored can be found.',
        tone: 'text-emerald-500',
        Icon: CheckCircle2,
    },
    attention: {
        label: 'Falling behind',
        detail: 'Nothing has failed, but some of what the assistant knows is no longer the newest.',
        tone: 'text-amber-500',
        Icon: Clock,
    },
    failing: {
        label: 'Needs attention',
        detail: 'Some of your data is missing from the assistant right now.',
        tone: 'text-red-500',
        Icon: XCircle,
    },
};

const RUN_STATUS_TONE: Record<CatalogSyncRunSummary['status'], string> = {
    success: 'text-emerald-500',
    error: 'text-red-500',
    running: 'text-blue-400',
};

const ago = (iso?: string): string => {
    if (!iso) return 'never';
    const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
};

const duration = (ms?: number): string => {
    if (ms === undefined) return '—';
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.round(ms / 1000);
    if (seconds < 90) return `${seconds}s`;
    return `${Math.round(seconds / 60)}m`;
};

const when = (iso: string): string => new Date(iso).toLocaleString();

/**
 * The number that matters is not how much is stored — it is how much of it no search can return,
 * because that is the gap between what a merchant believes the assistant knows and what it knows.
 */
const notSearchable = (index: DocumentIngestionHealth): number =>
    Math.max(index.total - index.indexed, 0);

const Figure: React.FC<{ label: string; value: string | number; tone?: string; hint?: string }> = ({
    label,
    value,
    tone = 'dark:text-text-primary text-light-text-primary',
    hint,
}) => (
    <div>
        <p className="text-sm dark:text-text-secondary text-light-text-secondary">{label}</p>
        <p className={`text-2xl font-semibold ${tone}`}>{value}</p>
        {hint && <p className="text-xs dark:text-text-secondary text-light-text-secondary mt-1">{hint}</p>}
    </div>
);

const DataHealthPage: React.FC = () => {
    const [report, setReport] = useState<DataHealthReport | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { setSubscriptionError } = useSubscriptionError();

    const load = useCallback(async () => {
        try {
            setReport(await api.getDataHealth());
            setError(null);
        } catch (err) {
            handleApiError(err, setError, setSubscriptionError);
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [setSubscriptionError]);

    useEffect(() => {
        load();
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [load]);

    const refresh = () => {
        setIsRefreshing(true);
        load();
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="w-6 h-6 animate-spin dark:text-text-secondary text-light-text-secondary" />
            </div>
        );
    }

    if (!report) {
        return (
            <Card title="Data Health">
                <p className="dark:text-text-secondary text-light-text-secondary">
                    {error || 'Could not read your data health.'}
                </p>
            </Card>
        );
    }

    const verdict = VERDICTS[report.state];
    const { index, catalog, runs } = report;
    const missing = notSearchable(index);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">Data Health</h1>
                    <p className="dark:text-text-secondary text-light-text-secondary mt-1">
                        What synced, what failed, and what is stale.
                    </p>
                </div>
                <Button variant="secondary" onClick={refresh} disabled={isRefreshing}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {error && (
                <Card>
                    <p className="text-red-500">{error}</p>
                </Card>
            )}

            <Card>
                <div className="flex items-start gap-4">
                    <verdict.Icon className={`w-10 h-10 shrink-0 ${verdict.tone}`} />
                    <div>
                        <p className={`text-2xl font-semibold ${verdict.tone}`}>{verdict.label}</p>
                        <p className="dark:text-text-secondary text-light-text-secondary mt-1">
                            {verdict.detail}
                        </p>
                    </div>
                </div>
            </Card>

            <Card title="What the assistant can find">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    <Figure label="Stored" value={index.total.toLocaleString()} />
                    <Figure
                        label="Searchable"
                        value={index.indexed.toLocaleString()}
                        tone="text-emerald-500"
                    />
                    <Figure
                        label="Not yet searchable"
                        value={missing.toLocaleString()}
                        tone={missing > 0 ? 'text-amber-500' : undefined}
                        hint={
                            index.oldestWaitingSince
                                ? `oldest waiting since ${ago(index.oldestWaitingSince)}`
                                : undefined
                        }
                    />
                    <Figure
                        label="Failed"
                        value={index.failed.toLocaleString()}
                        tone={index.failed > 0 ? 'text-red-500' : undefined}
                        hint={index.lastIndexedAt ? `last indexed ${ago(index.lastIndexedAt)}` : undefined}
                    />
                </div>

                {index.byType.length > 1 && (
                    <div className="mt-6 space-y-2">
                        {index.byType.map(entry => (
                            <div key={entry.type} className="flex items-center gap-3 text-sm">
                                <span className="w-32 shrink-0 dark:text-text-secondary text-light-text-secondary">
                                    {entry.type}
                                </span>
                                <div className="flex-1 h-2 rounded bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                    <div
                                        className="h-full bg-emerald-500"
                                        style={{
                                            width: `${entry.total ? (entry.indexed / entry.total) * 100 : 0}%`,
                                        }}
                                    />
                                </div>
                                <span className="w-28 text-right dark:text-text-secondary text-light-text-secondary">
                                    {entry.indexed.toLocaleString()} / {entry.total.toLocaleString()}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {index.failures.length > 0 && (
                <Card title="Why documents were not indexed">
                    <div className="space-y-4">
                        {index.failures.map(failure => (
                            <div key={failure.reason}>
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 mt-1 shrink-0 text-red-500" />
                                    <div>
                                        <p className="dark:text-text-primary text-light-text-primary">
                                            {failure.reason}
                                        </p>
                                        <p className="text-sm dark:text-text-secondary text-light-text-secondary">
                                            {failure.count.toLocaleString()} document
                                            {failure.count === 1 ? '' : 's'}
                                            {failure.sample.length > 0 && ` — e.g. ${failure.sample.join(', ')}`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {catalog && (
                <Card title="Catalog connection">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                        <Figure
                            label="Last successful sync"
                            value={ago(catalog.lastSuccessfulSyncAt)}
                            tone={catalog.overdue ? 'text-amber-500' : undefined}
                            hint={catalog.overdue ? 'later than the schedule allows' : undefined}
                        />
                        <Figure label="Last attempt" value={ago(catalog.lastAttemptedSyncAt)} />
                        <Figure label="Mode" value={catalog.syncMode} />
                        <Figure
                            label="Status"
                            value={catalog.lastSyncStatus}
                            tone={catalog.lastSyncStatus === 'error' ? 'text-red-500' : undefined}
                        />
                    </div>
                    {catalog.lastSyncError && (
                        <p className="mt-4 text-sm text-red-500 break-words">{catalog.lastSyncError}</p>
                    )}
                </Card>
            )}

            <Card title="Recent syncs">
                {runs.length === 0 ? (
                    <p className="dark:text-text-secondary text-light-text-secondary">
                        No sync has run since this started being recorded.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="dark:text-text-secondary text-light-text-secondary">
                                <tr className="text-left">
                                    <th className="py-2 pr-4 font-medium">Started</th>
                                    <th className="py-2 pr-4 font-medium">Trigger</th>
                                    <th className="py-2 pr-4 font-medium">Took</th>
                                    <th className="py-2 pr-4 font-medium text-right">Fetched</th>
                                    <th className="py-2 pr-4 font-medium text-right">Written</th>
                                    <th className="py-2 pr-4 font-medium text-right">Removed</th>
                                    <th className="py-2 pr-4 font-medium text-right">Re-indexed</th>
                                    <th className="py-2 pr-4 font-medium text-right">Rejected</th>
                                    <th className="py-2 font-medium">Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {runs.map(run => (
                                    <tr
                                        key={run.id}
                                        className="border-t dark:border-border-color border-light-border-color"
                                    >
                                        <td className="py-2 pr-4 whitespace-nowrap">{when(run.startedAt)}</td>
                                        <td className="py-2 pr-4">
                                            {run.trigger}
                                            {run.fullResync && ' · full'}
                                        </td>
                                        <td className="py-2 pr-4">{duration(run.durationMs)}</td>
                                        <td className="py-2 pr-4 text-right">
                                            {(
                                                run.productsFetched +
                                                run.categoriesFetched +
                                                run.variationsFetched
                                            ).toLocaleString()}
                                        </td>
                                        <td className="py-2 pr-4 text-right">
                                            {run.documentsUpserted.toLocaleString()}
                                        </td>
                                        <td className="py-2 pr-4 text-right">
                                            {run.documentsDeleted.toLocaleString()}
                                        </td>
                                        {/* The only column here that costs money: a sync that
                                            changed one description and one that invalidated the
                                            whole catalogue used to look identical. */}
                                        <td className="py-2 pr-4 text-right">
                                            {run.queuedForEmbedding.toLocaleString()}
                                        </td>
                                        {/* A run can be green and still have dropped records our
                                            own contract refused. Without the number, that reads
                                            exactly like a clean run. */}
                                        <td
                                            className={`py-2 pr-4 text-right ${run.contractViolations > 0 ? 'text-amber-500' : ''}`}
                                        >
                                            {run.contractViolations.toLocaleString()}
                                        </td>
                                        <td className={`py-2 ${RUN_STATUS_TONE[run.status]}`}>
                                            {run.error || run.status}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default DataHealthPage;
